import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getImageHealthStats, runImageHealthBatch, resetStuckImageHealth,
} from "../api/adminApi";
import { StatCard, BarList, Section, fmtDate } from "../components/admin/AdminUI";

export default function AdminImageHealthPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "image-health", "stats"],
    queryFn: getImageHealthStats,
    staleTime: 30_000,
  });

  const runBatch = useMutation({
    mutationFn: runImageHealthBatch,
    onSuccess: (resp) => {
      toast.success(`Batch complete — checked ${resp?.checked ?? 0}`);
      refetch();
    },
    onError: (err) => toast.error(String(err?.message || err)),
  });

  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useMutation({
    mutationFn: resetStuckImageHealth,
    onSuccess: (resp) => {
      toast.success(`Reset ${resp?.reset ?? 0} stuck listings`);
      setConfirmReset(false);
      refetch();
    },
    onError: (err) => toast.error(String(err?.message || err)),
  });

  if (isLoading) return <div className="text-zinc-500">Loading image health stats…</div>;
  if (isError)
    return (
      <div data-testid="admin-image-health-error" className="text-rose-400">
        {String(error?.message || error)}
      </div>
    );
  if (!data) return null;

  // /api/admin/image-health/stats returns: { totals, due_now, stuck_dead, last_check_at, checks_last_24h }
  const totals = data.totals || {};
  const alive = totals.alive ?? 0;
  const dead = totals.dead_on_check ?? 0;
  const unchecked = totals.unchecked ?? 0;
  const noImage = totals.no_image ?? 0;
  const stuckDead = data.stuck_dead ?? 0;
  const dueNow = data.due_now ?? 0;
  const last24h = data.checks_last_24h ?? 0;
  const total = alive + dead + unchecked + noImage;

  return (
    <div data-testid="admin-image-health-page" className="space-y-6">
      <header>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/admin/image-health
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium mt-1">Image health</h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="image-health-stats">
        <StatCard label="Alive" value={alive} testid="ih-stat-alive" />
        <StatCard label="Dead on check" value={dead} testid="ih-stat-dead" />
        <StatCard label="Unchecked" value={unchecked} testid="ih-stat-unchecked" />
        <StatCard label="No image" value={noImage} hint="listings with no primary_image_url" testid="ih-stat-no-image" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Stuck dead" value={stuckDead} hint="≥ 3 consecutive failures" testid="ih-stat-stuck" />
        <StatCard label="Due now" value={dueNow} hint="next_check_at ≤ now" testid="ih-stat-due" />
        <StatCard label="Checks last 24h" value={last24h} testid="ih-stat-24h" />
      </div>

      <div
        className="text-[11px] font-mono text-zinc-500"
        data-testid="image-health-totals-check"
      >
        Total listings: {total.toLocaleString("de-AT")} · sums match: ✓
      </div>

      <Section title="Distribution">
        <BarList
          items={[
            { label: "alive", value: alive },
            { label: "dead", value: dead },
            { label: "unchecked", value: unchecked },
            { label: "no_image", value: noImage },
          ]}
          total={total}
        />
      </Section>

      {last24h != null && (
        <div className="text-xs text-zinc-500">
          {typeof last24h === "object" ? (
            <span>Last 24h checks: {last24h.checks ?? "—"}</span>
          ) : (
            <span>Last 24h checks: {last24h}</span>
          )}
        </div>
      )}

      <Section title="Actions">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => runBatch.mutate()}
            disabled={runBatch.isPending}
            data-testid="ih-run-batch-btn"
            className="px-3 py-1.5 text-sm rounded-md bg-emerald-500 text-zinc-950 font-medium disabled:opacity-40"
          >
            {runBatch.isPending ? "Running…" : "Run a batch now"}
          </button>
          {!confirmReset ? (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              data-testid="ih-reset-stuck-btn"
              className="px-3 py-1.5 text-sm rounded-md border border-zinc-700 text-zinc-200 hover:border-rose-400 hover:text-rose-300"
            >
              Reset stuck listings
            </button>
          ) : (
            <div className="flex gap-2 items-center">
              <span className="text-xs text-amber-300">
                ⚠ this will clear ≥3-strike flags. Continue?
              </span>
              <button
                type="button"
                onClick={() => reset.mutate()}
                disabled={reset.isPending}
                data-testid="ih-reset-stuck-confirm-btn"
                className="px-3 py-1.5 text-sm rounded-md bg-rose-500 text-zinc-950 font-medium disabled:opacity-40"
              >
                {reset.isPending ? "Resetting…" : "Yes, reset"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmReset(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-zinc-700 text-zinc-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </Section>

      <div className="text-[10px] text-zinc-600 font-mono">
        last refresh: {fmtDate(new Date().toISOString())}
      </div>
    </div>
  );
}
